import { TIME_PERIODS } from '@/common/constants';
import { debug } from '@/utils/utils';
import { ThemeConfig, TimePeriod } from '@nylas/core';
import { Element, Event, EventEmitter, Watch } from '@stencil/core';
import { Component, h, Listen, Prop, State } from '@stencil/core';
import i18next from 'i18next';

const pluralToSingular = {
  hours: 'hour',
  days: 'day',
  weeks: 'week',
  months: 'month',
  years: 'year',
};

/**
 * The time period selector component allows the user to select a time period and a number value for that time period.
 */
@Component({
  tag: 'time-period-selector',
  styleUrl: 'time-period-selector.scss',
  scoped: true,
})
export class TimePeriodSelector {
  @Element() host!: HTMLElement;

  /**
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  // The possible values for the time periods dropdown
  @Prop() timePeriods: TimePeriod[] = TIME_PERIODS;

  /**
   * The default selected time period.
   */
  @Prop() defaultSelectedPeriod!: string;

  /**
   * The default selected number.
   */
  @Prop() defaultSelectedNumber!: number;

  /**
   * The error state
   */
  @Prop() hasError: boolean = false;

  /**
   * The currently selected time period
   */
  @State() selectedPeriod: string = this.defaultSelectedPeriod;

  /**
   * The currently selected number of the time period
   */
  @State() selectedNumber: number = this.defaultSelectedNumber;

  /*
   * The options for the number dropdown, to be calculated based on the selectedPeriod
   */
  @State() numberOptions: { label: string; value: string }[] = this.calculateOptions(this.defaultSelectedPeriod || 'hour').map(i => {
    return {
      label: i.toString(),
      value: i.toString(),
    };
  });

  /**
   * The options for the time period dropdown
   */
  @State() timePeriodOptions = this.timePeriods.map(i => {
    return {
      label: i.label,
      value: i.value,
    };
  });

  @Watch('defaultSelectedPeriod')
  defaultSelectedPeriodChanged(newValue: string) {
    this.selectedPeriod = newValue;
    this.updateNumberOptionsAndSelectedNumber(newValue);
  }

  @Watch('defaultSelectedNumber')
  defaultSelectedNumberChanged(newValue: number) {
    this.selectedNumber = newValue;
  }

  @Watch('timePeriods')
  timePeriodsChanged(newValue: { label: string; value: string }[]) {
    this.timePeriodOptions = [...newValue];
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  connectedCallback() {
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  componentDidLoad() {
    this.selectedNumber = this.defaultSelectedNumber;
    this.selectedPeriod = this.defaultSelectedPeriod;
    const period = pluralToSingular[this.selectedPeriod] ?? this.selectedPeriod;
    const numberOptions = this.calculateOptions(period);
    this.numberOptions = numberOptions.map(i => {
      return {
        label: i.toString(),
        value: i.toString(),
      };
    });
  }

  @Event() timePeriodChanged!: EventEmitter<{ number: number; period: string }>;

  private calculateOptions(period: string): number[] {
    switch (period) {
      case 'hour':
        return Array.from({ length: 23 }, (_, i) => i + 1);
      case 'minute':
        return Array.from({ length: 13 }, (_, i) => i * 5);
      case 'day':
        return Array.from({ length: 30 }, (_, i) => i + 1);
      case 'week':
        return Array.from({ length: 4 }, (_, i) => i + 1);
      case 'month':
        return Array.from({ length: 12 }, (_, i) => i + 1);
      case 'year':
        return Array.from({ length: 10 }, (_, i) => i + 1);
      default:
        return [];
    }
  }

  @Listen('inputOptionChanged')
  inputOptionChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    debug('time-period-selector', 'inputOptionChangedHandler', event.detail);
    const { value, name } = event.detail;
    if (name === 'time-number') {
      this.selectedNumber = value ? parseInt(value) : this.defaultSelectedNumber;
    }
    const selected = {
      number: this.selectedNumber,
      period: this.selectedPeriod,
    };
    this.timePeriodChanged.emit(selected);
  }

  @Listen('nylasFormDropdownChanged')
  nylasFormDropdownChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    debug('time-period-selector', 'nylasFormDropdownChangedHandler', event.detail);
    const { value, name } = event.detail;
    if (name === 'time-period') {
      this.selectedPeriod = value;
      this.updateNumberOptionsAndSelectedNumber(value);
    } else if (name === 'time-number') {
      this.selectedNumber = parseInt(value);
    }
    const selected = {
      number: this.selectedNumber,
      period: this.selectedPeriod,
    };
    this.timePeriodChanged.emit(selected);
  }

  updateNumberOptionsAndSelectedNumber(period: string) {
    const numberOptions = this.calculateOptions(period);
    this.numberOptions = numberOptions.map(i => {
      return {
        label: i.toString(),
        value: i.toString(),
      };
    });
  }

  render() {
    return (
      <div class="time-period-selector">
        {typeof this.selectedNumber == 'number' && (
          <input-dropdown
            id="time-number"
            name={'time-number'}
            class={{ error: this.hasError }}
            inputValue={this.selectedNumber.toString()}
            exportparts="id_dropdown: tps__number-dropdown, id_dropdown-input: tps__number-dropdown-button, id_dropdown-content: tps__number-dropdown-content"
            options={this.numberOptions}
            defaultInputOption={this.numberOptions.find(i => i.value == this.selectedNumber.toString())}
            themeConfig={this.themeConfig}
          />
        )}
        {typeof this.selectedPeriod == 'string' && (
          <select-dropdown
            id="time-period"
            name={'time-period'}
            options={this.timePeriodOptions}
            exportparts="sd_dropdown: tps__period-dropdown, sd_dropdown-button: tps__period-dropdown-button, sd_dropdown-content: tps__period-dropdown-content"
            pluralizedLabel={this.selectedNumber > 1 ? i18next.t(`time.${this.selectedPeriod}s`) : i18next.t(`time.${this.selectedPeriod}`)}
            defaultSelectedOption={this.timePeriodOptions?.find(i => i.value == this.selectedPeriod)}
            withSearch={false}
            themeConfig={this.themeConfig}
          />
        )}
      </div>
    );
  }
}
